import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Globe, Mic } from 'lucide-react';

// Define language and accent types
type AccentOption = {
  id: string;
  name: string;
  voiceId: string;
};

type AccentsType = {
  [key: string]: AccentOption[];
};

// Hardcoded data from language_accents.csv
const LANGUAGES = [
  { id: 'Arabic', name: 'Arabic' },
  { id: 'Chinese', name: 'Chinese' },
  { id: 'English', name: 'English' },
  { id: 'French', name: 'French' },
  { id: 'German', name: 'German' }
];

const ACCENTS: AccentsType = {
  'Arabic': [
    { id: 'modern standard', name: 'modern standard', voiceId: 'a1KZUXKFVFDOb33I1uqr' }
  ],
  'Chinese': [
    { id: 'Mandarin Beijing', name: 'Mandarin Beijing', voiceId: 'bhJUNIXWQQ94l8eI2VUf' }
  ],
  'English': [
    { id: 'American', name: 'American', voiceId: 'g6xIsTj2HwM6VR4iXFCw' },
    { id: 'Australian', name: 'Australian', voiceId: 'M7ya1YbaeFaPXljg9BpK' },
    { id: 'British', name: 'British', voiceId: 'Fahco4VZzobUeiPqni1S' },
    { id: 'Indian', name: 'Indian', voiceId: '90ipbRoKi4CpHXvKVtl0' }
  ],
  'French': [
    { id: 'Standard', name: 'Standard', voiceId: 'MNKK2Wl2wbbsEPQTHZGt' }
  ],
  'German': [
    { id: 'Standard', name: 'Standard', voiceId: 'aTTiK3YzK3dXETpuDE2h' }
  ]
};

export interface InterviewerSettings {
  language: string;
  accent: string;
  voiceId: string;
}

interface AIAssessmentSettingsStepProps {
  settings: InterviewerSettings;
  onSettingsChange: (settings: InterviewerSettings) => void;
  onSubmit: () => void;
  onBack: () => void;
  customQuestions: string[];
  role?: string;
  level?: 'entry' | 'mid' | 'senior';
}

const AIAssessmentSettingsStep: React.FC<AIAssessmentSettingsStepProps> = ({
  settings,
  onSettingsChange,
  onSubmit,
  onBack,
  customQuestions,
  role = '',
  level = 'mid'
}) => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Set default values if none are provided
    if (!settings.language && LANGUAGES.length > 0) {
      const firstLanguage = LANGUAGES[0].id;
      const firstAccent = ACCENTS[firstLanguage]?.[0];
      
      if (firstAccent) {
        onSettingsChange({
          language: firstLanguage,
          accent: firstAccent.id,
          voiceId: firstAccent.voiceId
        });
      }
    }
  }, []);

  const handleLanguageChange = (newLanguage: string) => {
    // Reset accent when language changes
    const availableAccents = ACCENTS[newLanguage] || [];
    if (availableAccents.length > 0) {
      onSettingsChange({
        language: newLanguage,
        accent: availableAccents[0].id,
        voiceId: availableAccents[0].voiceId
      });
    }
  };

  const handleAccentChange = (newAccent: string) => {
    // Find the accent object to get the voiceId
    const accentObj = ACCENTS[settings.language]?.find((accent: AccentOption) => accent.id === newAccent);
    if (accentObj) {
      onSettingsChange({
        ...settings,
        accent: newAccent,
        voiceId: accentObj.voiceId
      });
    }
  };

  return (
    <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-10 backdrop-blur-sm">
      <h1 className="text-3xl font-bold text-center mb-3 text-[#1A1F2C]">Interviewer Settings</h1>
      
      <p className="text-center text-[#1A1F2C]/80 mb-8">
        Configure the AI interviewer settings for the {level} {role} assessment
      </p>
      
      <div className="space-y-8">
        <div className="bg-[#F8F7FF] rounded-lg p-6 border border-[#9b87f5]/20 space-y-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-[#1A1F2C] mb-2">Voice & Language Settings</h2>
            <p className="text-[#1A1F2C]/70 text-sm">
              Choose the language and accent the AI interviewer will use during the assessment
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
            <div className="space-y-3">
              <div className="flex items-center">
                <Globe className="text-[#9b87f5] mr-2" size={18} />
                <Label className="text-[#1A1F2C] font-medium">Interview Language</Label>
              </div>
              <Select value={settings.language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang.id} value={lang.id}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <Mic className="text-[#9b87f5] mr-2" size={18} />
                <Label className="text-[#1A1F2C] font-medium">AI Accent</Label>
              </div>
              <Select value={settings.accent} onValueChange={handleAccentChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select accent" />
                </SelectTrigger>
                <SelectContent>
                  {(ACCENTS[settings.language] || []).map((accentOption: AccentOption) => (
                    <SelectItem key={accentOption.id} value={accentOption.id}>
                      {accentOption.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex justify-between mt-10">
        <Button 
          variant="outline" 
          className="border-[#9b87f5]/30 bg-white hover:bg-[#9b87f5]/10 text-[#1A1F2C]/70 hover:text-[#1A1F2C]"
          onClick={onBack}
        >
          Back
        </Button>
        <Button 
          variant="purple"
          className="px-8 py-6 bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] shadow-md"
          onClick={onSubmit}
          disabled={isLoading}
        >
          Continue to Review
        </Button>
      </div>
    </div>
  );
};

export default AIAssessmentSettingsStep; 